import { useState, useEffect } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Switch } from "@/components/ui/switch"
import { AnimatedCard } from "./AnimatedCard"
import { GlassCard } from "./GlassCard"
import { useApexState } from "@/hooks/useApexState"
import { 
  Bell, 
  BellRing,
  Crown,
  ShoppingBag,
  Trophy,
  Heart,
  Users,
  Zap,
  Calendar,
  Star,
  X,
  Settings,
  Check,
  Archive,
  Pin,
  Filter
} from "lucide-react"

interface SmartNotification {
  id: string
  type: 'drop_alert' | 'social' | 'achievement' | 'challenge' | 'tier' | 'event' | 'recommendation' | 'system'
  priority: 'low' | 'medium' | 'high' | 'urgent'
  title: string
  content: string
  timestamp: string
  isRead: boolean
  isPinned: boolean
  isArchived: boolean
  actionRequired: boolean
  aiGenerated?: boolean
  metadata?: {
    userId?: string
    productId?: string
    challengeId?: string
    eventId?: string
    image?: string
    cta?: {
      label: string
      action: string
    }
  }
  expiresAt?: string
  interactions?: {
    viewed: boolean
    clicked: boolean
    dismissed: boolean
  }
}

interface SmartNotificationsProps {
  isOpen: boolean
  onClose: () => void
}

const mockNotifications: SmartNotification[] = [
  {
    id: "notif-1",
    type: "drop_alert",
    priority: "urgent",
    title: "🔥 Architect Collection - Final Hours",
    content: "Only 12 pieces left! Your wishlisted Architect Hoodie is still available but selling fast.",
    timestamp: "Just now",
    isRead: false,
    isPinned: true,
    isArchived: false,
    actionRequired: true,
    aiGenerated: true,
    metadata: {
      productId: "architect-hoodie",
      image: "https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=100&h=100&fit=crop",
      cta: {
        label: "Secure Now",
        action: "view_product"
      }
    },
    expiresAt: "2024-02-01T23:59:59Z",
    interactions: {
      viewed: false,
      clicked: false,
      dismissed: false
    }
  },
  {
    id: "notif-2",
    type: "achievement",
    priority: "high",
    title: "🏆 Style Master Unlocked!",
    content: "Congratulations! You've completed 10 style challenges and earned the coveted Style Master badge. You're now in the top 5% of creators.",
    timestamp: "2 hours ago",
    isRead: false,
    isPinned: false,
    isArchived: false,
    actionRequired: false,
    metadata: {
      image: "https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=100&h=100&fit=crop",
      cta: {
        label: "View Badge",
        action: "view_achievements"
      }
    },
    interactions: {
      viewed: false,
      clicked: false,
      dismissed: false
    }
  },
  {
    id: "notif-3",
    type: "recommendation",
    priority: "medium",
    title: "🎯 Perfect Match Found",
    content: "Based on your recent purchases and style preferences, we think you'll love the new Kinetic Tank. Available in your preferred sage color.",
    timestamp: "4 hours ago",
    isRead: false,
    isPinned: false,
    isArchived: false,
    actionRequired: false,
    aiGenerated: true,
    metadata: {
      productId: "kinetic-tank",
      image: "https://images.unsplash.com/photo-1583900985737-6d0495555783?w=100&h=100&fit=crop",
      cta: {
        label: "View Item",
        action: "view_product"
      }
    },
    interactions: {
      viewed: false,
      clicked: false,
      dismissed: false
    }
  },
  {
    id: "notif-4",
    type: "social",
    priority: "medium",
    title: "💖 Your Post is Trending",
    content: "Your morning movement post has received 100+ likes and is now featured in the community spotlight!",
    timestamp: "6 hours ago",
    isRead: true,
    isPinned: false,
    isArchived: false,
    actionRequired: false,
    metadata: {
      image: "https://images.unsplash.com/photo-1556905055-8f358a7a47b2?w=100&h=100&fit=crop",
      cta: {
        label: "View Post",
        action: "view_post"
      }
    },
    interactions: {
      viewed: true,
      clicked: true,
      dismissed: false
    }
  },
  {
    id: "notif-5",
    type: "challenge",
    priority: "medium",
    title: "⏰ Challenge Deadline Approaching",
    content: "Only 8 hours left to submit your Motion & Form challenge entry. You're so close to completing this month's challenge series!",
    timestamp: "8 hours ago",
    isRead: true,
    isPinned: false,
    isArchived: false,
    actionRequired: true,
    metadata: {
      challengeId: "motion-form",
      cta: {
        label: "Submit Entry",
        action: "submit_challenge"
      }
    },
    interactions: {
      viewed: true,
      clicked: false,
      dismissed: false
    }
  },
  {
    id: "notif-6",
    type: "tier",
    priority: "high",
    title: "👑 Architect Tier Within Reach",
    content: "You're just 320 points away from Architect status! Complete one more challenge or make a purchase to unlock exclusive benefits.",
    timestamp: "1 day ago",
    isRead: false,
    isPinned: false,
    isArchived: false,
    actionRequired: false,
    aiGenerated: true,
    metadata: {
      cta: {
        label: "View Progress",
        action: "view_tier_progress"
      }
    },
    interactions: {
      viewed: false,
      clicked: false,
      dismissed: false
    }
  },
  {
    id: "notif-7",
    type: "event",
    priority: "low",
    title: "📅 NYC Run Club Tomorrow",
    content: "Don't forget about tomorrow's run club meetup in Central Park. Weather looks perfect for a morning run!",
    timestamp: "1 day ago",
    isRead: true,
    isPinned: false,
    isArchived: false,
    actionRequired: false,
    metadata: {
      eventId: "nyc-run-club",
      cta: {
        label: "View Details",
        action: "view_event"
      }
    },
    interactions: {
      viewed: true,
      clicked: false,
      dismissed: false
    }
  }
]

export function SmartNotifications({ isOpen, onClose }: SmartNotificationsProps) {
  const { state } = useApexState()
  const [notifications, setNotifications] = useState<SmartNotification[]>(mockNotifications)
  const [selectedFilter, setSelectedFilter] = useState<string>("all")
  const [showSettings, setShowSettings] = useState(false)
  const [notificationSettings, setNotificationSettings] = useState({
    instant: true,
    daily_digest: true,
    weekly_summary: false,
    ai_recommendations: true,
    marketing: false,
    social: true,
    challenges: true,
    drops: true
  })

  const filters = [
    { id: "all", label: "All", count: notifications.length },
    { id: "unread", label: "Unread", count: notifications.filter(n => !n.isRead).length },
    { id: "urgent", label: "Urgent", count: notifications.filter(n => n.priority === 'urgent').length },
    { id: "pinned", label: "Pinned", count: notifications.filter(n => n.isPinned).length }
  ]

  const filteredNotifications = notifications.filter(notification => {
    if (notification.isArchived) return false
    
    switch (selectedFilter) {
      case "unread":
        return !notification.isRead
      case "urgent":
        return notification.priority === "urgent"
      case "pinned":
        return notification.isPinned
      default:
        return true
    }
  })

  const getNotificationIcon = (type: SmartNotification['type']) => {
    switch (type) {
      case 'drop_alert':
        return <ShoppingBag size={16} className="text-primary" />
      case 'achievement':
        return <Trophy size={16} className="text-accent" />
      case 'social':
        return <Heart size={16} className="text-red-400" />
      case 'challenge':
        return <Zap size={16} className="text-yellow-500" />
      case 'tier':
        return <Crown size={16} className="text-tier-architect" />
      case 'event':
        return <Calendar size={16} className="text-blue-500" />
      case 'recommendation':
        return <Star size={16} className="text-primary" />
      case 'system':
        return <Bell size={16} className="text-muted-foreground" />
      default:
        return <Bell size={16} className="text-muted-foreground" />
    }
  }

  const getPriorityStyle = (priority: SmartNotification['priority']) => {
    switch (priority) {
      case 'urgent':
        return 'border-l-red-500 bg-red-500/5'
      case 'high':
        return 'border-l-primary bg-primary/5'
      case 'medium':
        return 'border-l-accent bg-accent/5'
      case 'low':
        return 'border-l-muted bg-muted/5'
    }
  }

  const handleNotificationClick = (notification: SmartNotification) => {
    // Mark as read
    setNotifications(prev => 
      prev.map(n => 
        n.id === notification.id 
          ? { ...n, isRead: true, interactions: { ...n.interactions, clicked: true } }
          : n
      )
    )

    // Handle CTA action
    if (notification.metadata?.cta) {
      console.log('Executing action:', notification.metadata.cta.action)
      // Route to appropriate section based on action
    }
  }

  const handlePin = (notificationId: string) => {
    setNotifications(prev =>
      prev.map(n =>
        n.id === notificationId ? { ...n, isPinned: !n.isPinned } : n
      )
    )
  }

  const handleArchive = (notificationId: string) => {
    setNotifications(prev =>
      prev.map(n =>
        n.id === notificationId ? { ...n, isArchived: true } : n
      )
    )
  }

  const handleMarkAllRead = () => {
    setNotifications(prev =>
      prev.map(n => ({ ...n, isRead: true }))
    )
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className="absolute top-0 right-0 w-full max-w-md h-full">
        <GlassCard className="h-full rounded-none rounded-l-lg">
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <BellRing size={20} className="text-foreground" />
                <h2 className="text-lg font-semibold text-foreground">Notifications</h2>
                {notifications.filter(n => !n.isRead && !n.isArchived).length > 0 && (
                  <Badge className="bg-primary text-black text-xs">
                    {notifications.filter(n => !n.isRead && !n.isArchived).length}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSettings(!showSettings)}
                >
                  <Settings size={16} />
                </Button>
                <Button variant="ghost" size="sm" onClick={onClose}>
                  <X size={20} />
                </Button>
              </div>
            </div>

            {/* Settings Panel */}
            {showSettings && (
              <div className="p-4 border-b border-white/10 bg-muted/5">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-foreground">Notification Preferences</h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleMarkAllRead}
                    >
                      Mark All Read
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    {Object.entries(notificationSettings).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between">
                        <label className="text-sm text-foreground capitalize">
                          {key.replace('_', ' ')}
                        </label>
                        <Switch
                          checked={value}
                          onCheckedChange={(checked) =>
                            setNotificationSettings(prev => ({ ...prev, [key]: checked }))
                          }
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Filter Tabs */}
            <div className="p-4 border-b border-white/10">
              <div className="flex gap-2 overflow-x-auto scrollbar-hide">
                {filters.map(filter => (
                  <Button
                    key={filter.id}
                    variant={selectedFilter === filter.id ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedFilter(filter.id)}
                    className="whitespace-nowrap"
                  >
                    {filter.label} ({filter.count})
                  </Button>
                ))}
              </div>
            </div>

            {/* Notifications List */}
            <div className="flex-1 overflow-y-auto">
              {filteredNotifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-center p-4">
                  <Bell size={48} className="text-muted-foreground opacity-50 mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    All caught up!
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    No new notifications to show
                  </p>
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredNotifications.map(notification => (
                    <AnimatedCard key={notification.id}>
                      <div
                        className={`
                          relative p-4 border-l-2 cursor-pointer transition-all duration-200
                          ${getPriorityStyle(notification.priority)}
                          ${!notification.isRead ? 'bg-primary/5' : 'hover:bg-white/5'}
                        `}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        {/* Priority Indicator */}
                        {notification.priority === 'urgent' && (
                          <div className="absolute top-2 right-2">
                            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                          </div>
                        )}

                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 mt-0.5">
                            {getNotificationIcon(notification.type)}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="font-medium text-foreground leading-tight pr-2">
                                {notification.title}
                                {notification.aiGenerated && (
                                  <Badge className="ml-2 bg-purple-500/20 text-purple-400 text-xs">
                                    AI
                                  </Badge>
                                )}
                              </h4>
                              
                              <div className="flex items-center gap-1">
                                {!notification.isRead && (
                                  <div className="w-2 h-2 bg-primary rounded-full" />
                                )}
                              </div>
                            </div>

                            <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                              {notification.content}
                            </p>

                            {/* Metadata */}
                            {notification.metadata?.image && (
                              <div className="flex items-center gap-2 mb-3">
                                <img
                                  src={notification.metadata.image}
                                  alt=""
                                  className="w-12 h-12 rounded-lg object-cover"
                                />
                                {notification.metadata.cta && (
                                  <Button
                                    size="sm"
                                    className="bg-primary hover:bg-primary/90 text-black"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleNotificationClick(notification)
                                    }}
                                  >
                                    {notification.metadata.cta.label}
                                  </Button>
                                )}
                              </div>
                            )}

                            {/* Footer */}
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-muted-foreground">
                                {notification.timestamp}
                              </span>
                              
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handlePin(notification.id)
                                  }}
                                  className={`p-1 rounded hover:bg-white/10 transition-colors ${
                                    notification.isPinned ? 'text-accent' : 'text-muted-foreground'
                                  }`}
                                >
                                  <Pin size={12} />
                                </button>
                                
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleArchive(notification.id)
                                  }}
                                  className="p-1 rounded hover:bg-white/10 transition-colors text-muted-foreground"
                                >
                                  <Archive size={12} />
                                </button>
                              </div>
                            </div>

                            {/* Expiration */}
                            {notification.expiresAt && (
                              <div className="mt-2 p-2 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                                <p className="text-xs text-yellow-400">
                                  ⏰ Expires soon - Take action before it's gone!
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </AnimatedCard>
                  ))}
                </div>
              )}
            </div>
          </div>
        </GlassCard>
      </div>
    </div>
  )
}